import { useId, type ChangeEvent, type MouseEventHandler } from 'react';
import { Checkbox, CheckboxProps, FormControlLabel, styled } from '@mui/material';
import {
  Checkbox as FplCheckbox,
  HiddenLabel as FplHiddenLabel,
  Label as FplLabel,
  ManuallyLabeledCheckbox as FplManuallyLabeledCheckbox,
} from '@figma/fpl-components';
import { CheckMarkIcon } from '@/UI/Icons/CheckMarkIcon';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { getFplDataAttributes } from '@/UI/Buttons/fplDataAttributes';

const SIZE_CONFIG = {
  small: {
    checkbox: 14,
    icon: 10,
  },
  medium: {
    checkbox: 18,
    icon: 14,
  },
  large: {
    checkbox: 18,
    icon: 14,
  },
};

const StyledFormControlLabel = styled(FormControlLabel)({
  margin: 0,
  '& .MuiFormControlLabel-label': {
    marginLeft: 8,
    fontSize: '0.75rem',
    fontWeight: 400,
  },
  '&.Mui-disabled': {
    '& .MuiFormControlLabel-label': {
      color: color.White16_T,
    },
  },
});

const StyledCheckbox = styled(Checkbox)(({ size = 'medium' }) => {
  return {
    padding: 0,
    width: SIZE_CONFIG[size].checkbox,
    height: SIZE_CONFIG[size].checkbox,
    borderRadius: 2,

    '&.MuiCheckbox-root': {
      backgroundColor: 'transparent',
      border: `1px solid ${color.White64_T}`,
      padding: `0px !important`,
    },

    '&.Mui-checked': {
      backgroundColor: color.White80_T,
      border: 'none',

      '&:hover': {
        backgroundColor: color.White100,
        '& .CheckMarkIcon': {
          color: color.Black92,
        },
      },
      '& .CheckMarkIcon': {
        color: color.Black92,
      },
    },

    '&:hover': {
      backgroundColor: color.White04_T,
      border: `1px solid ${color.White80_T}`,
    },

    '&.Mui-disabled': {
      backgroundColor: color.White04_T,
      border: `1px solid ${color.White04_T}`,

      '&.Mui-checked': {
        backgroundColor: color.White04_T,
        border: 'none',
        '& .CheckMarkIcon': {
          color: color.White16_T,
        },
      },
    },

    '& .MuiSvgIcon-root': {
      display: 'none',
    },
    '& svg': {
      width: SIZE_CONFIG[size].icon,
      height: SIZE_CONFIG[size].icon,
    },
  };
});

export type AppCheckboxProps = Omit<CheckboxProps, 'checkedIcon' | 'onClickCapture'> & {
  label?: string;
  /**
   * Runs before the checkbox handles a click. Capture is the phase to use because the FPL checkbox
   * supplies its own `onClick`; the element type is widened so one handler suits both branches
   * (MUI hands over its root button, FPL the input).
   */
  onClickCapture?: MouseEventHandler<HTMLElement>;
};

export const AppCheckbox = ({ label, size = 'medium', ...props }: AppCheckboxProps) => {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const generatedId = useId();

  if (isFplMigrationEnabled) {
    const {
      checked,
      defaultChecked,
      disabled,
      indeterminate,
      onChange,
      onClickCapture,
      id,
      name,
      required,
      inputProps,
    } = props;
    const ariaLabel = inputProps?.['aria-label'];

    const handleChange = onChange
      ? (nextChecked: boolean, { event }: { event: ChangeEvent<HTMLInputElement> }) => onChange(event, nextChecked)
      : undefined;

    const sharedProps = {
      ...getFplDataAttributes(props),
      checked,
      defaultChecked,
      disabled,
      mixed: indeterminate,
      onChange: handleChange,
      // FPL's checkbox owns `onClick` (it toggles from there), so a caller that needs to run
      // alongside a click has to do it in the capture phase.
      onClickCapture,
      name,
      required,
    };

    // A visible label renders as an FPL Label; a label supplied only for
    // screen readers (via inputProps.aria-label) renders as a HiddenLabel so the
    // input keeps a properly associated <label htmlFor> instead of a bare aria-label.
    if (label != null && label !== '') {
      return <FplCheckbox {...sharedProps} label={<FplLabel>{label}</FplLabel>} />;
    }

    if (ariaLabel != null && ariaLabel !== '') {
      return <FplCheckbox {...sharedProps} label={<FplHiddenLabel>{ariaLabel}</FplHiddenLabel>} />;
    }

    return <FplManuallyLabeledCheckbox {...sharedProps} id={id ?? generatedId} />;
  }

  return (
    <StyledFormControlLabel
      label={label}
      control={<StyledCheckbox {...props} size={size} checkedIcon={<CheckMarkIcon className="CheckMarkIcon" />} />}
    />
  );
};
